import classNames from 'classnames';
import { ChangeEventHandler, FocusEventHandler, ReactNode, useMemo } from 'react';

import { PropsWithClassname } from '@/app/types';

export interface SelectOption<ValueType = string> {
  value: ValueType | undefined;
  label: string;
  disabled?: boolean;
  highlighted?: boolean;
}

export interface SelectProps<ValueType = string> extends PropsWithClassname {
  name: string;
  value: ValueType | undefined;
  onChange: ChangeEventHandler<HTMLSelectElement>;
  onBlur?: FocusEventHandler<HTMLSelectElement>;
  options: SelectOption<ValueType>[];
  label?: string;
  disabled?: boolean;
  isErrorVisible?: boolean;
  errorMessage?: ReactNode;
  note?: string;
}

function Select<ValueType extends string | number | readonly string[] = string>({
  name,
  value,
  options,
  onChange,
  onBlur,
  label,
  disabled,
  isErrorVisible,
  errorMessage,
  note,
  className,
}: SelectProps<ValueType>) {
  const sortedOptions = useMemo(() => {
    const highlightedOptions = options.filter((option) => option.highlighted);
    const remainingOptions = options.filter((option) => !option.highlighted);

    if (highlightedOptions.length && remainingOptions.length) {
      return [...highlightedOptions, { value: '-', label: '--------', disabled: true }, ...remainingOptions];
    }

    return [...highlightedOptions, ...remainingOptions];
  }, [options]);

  return (
    <div className={className} data-testid={`form-field-${name}`}>
      {!!label && <label className="block mb-[8px] font-medium text-[16px] leading-[24px]">{label}</label>}
      {!!errorMessage && isErrorVisible && <div className="text-warning font-medium mb-[8px]">{errorMessage}</div>}
      <select
        //it was triggering onclick event on parent which was closing RewardBox
        onClick={(ev) => ev.stopPropagation()}
        name={name}
        onChange={onChange}
        onBlur={onBlur}
        disabled={disabled}
        value={value}
        className={classNames(
          className,
          `appearance-none bg-[url('/public/images/arrow.svg')] bg-no-repeat bg-[right_6px_center] rotate w-full bg-white border border-neutral-light focus:border-neutral-dark hover:border-neutral-dark rounded text-neutral-dark px-[24px] py-[12px] outline-0`,
          {
            'text-neutral-mid': (disabled && !isErrorVisible) || value === undefined,
            'border-warning focus:border-warning has-error': isErrorVisible,
            'mb-[8px]': !!note,
            'mb-[32px]': !note,
          },
        )}
      >
        {sortedOptions.map((option) => (
          <option
            className={option.value !== undefined ? 'text-black' : 'text-neutral-semiTransparent'}
            key={String(option.value)}
            value={option.value}
            disabled={disabled || option.disabled}
          >
            {option.label}
          </option>
        ))}
      </select>
      {!!note && <small className="block text-[12px] leading-[1.4] text-neutral-mid mb-[32px]">{note}</small>}
    </div>
  );
}

export default Select;
